import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import DashboardLayout from "@/components/layout/DashboardLayout";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Package, Truck } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const STATUS_LABEL = {
  pending_payment: { label: "Awaiting payment", cls: "bg-amber-100 text-amber-800" },
  paid: { label: "Paid — awaiting shipment", cls: "bg-accent/15 text-accent" },
  shipped: { label: "Shipped", cls: "bg-primary/15 text-primary" },
  delivered: { label: "Delivered", cls: "bg-emerald-100 text-emerald-800" },
  cancelled: { label: "Cancelled", cls: "bg-red-100 text-red-700" },
};

function StatusPill({ status }) {
  const c = STATUS_LABEL[status] || STATUS_LABEL.pending_payment;
  return <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${c.cls}`}>{c.label}</span>;
}

function ShipDialog({ order, onSaved }) {
  const [open, setOpen] = useState(false);
  const [tracking, setTracking] = useState("");
  const submit = async () => {
    try { await api.patch(`/supplier/sample-orders/${order.id}`, { status: "shipped", tracking }); toast.success("Marked as shipped"); setOpen(false); onSaved(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" data-testid={`ship-${order.id}`}><Truck className="w-3.5 h-3.5 mr-1" />Mark shipped</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-heading tracking-tight">Ship {order.product_name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="text-sm text-muted-foreground">Shipping to {order.shipping_name}, {order.shipping_city}, {order.shipping_country}</div>
          <div><Label>Tracking number (optional)</Label><Input data-testid="ship-tracking" value={tracking} onChange={(e) => setTracking(e.target.value)} /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button data-testid="ship-save" onClick={submit}>Confirm shipment</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function SampleOrders() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [params, setParams] = useSearchParams();

  const endpoint = user.role === "admin" ? "/admin/sample-orders"
    : user.role === "supplier" ? "/supplier/sample-orders"
    : "/buyer/sample-orders";

  const load = () => api.get(endpoint).then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, [endpoint]);

  useEffect(() => {
    const paid = params.get("paid");
    if (paid) {
      api.get(`/sample-orders/status/${paid}`).then((r) => {
        if (r.data.payment_status === "paid") { toast.success("Payment confirmed — supplier notified."); load(); }
        else toast.info("Verifying payment…");
        params.delete("paid"); setParams(params, { replace: true });
      }).catch(() => {});
    }
    // eslint-disable-next-line
  }, []);

  return (
    <DashboardLayout title="Sample orders">
      <Card>
        <CardHeader><CardTitle className="font-heading tracking-tight">{items.length} sample order{items.length === 1 ? "" : "s"}</CardTitle></CardHeader>
        <CardContent>
          {items.length === 0 ? (
            <div className="py-12 text-center text-sm text-muted-foreground" data-testid="samples-empty">
              <Package className="w-10 h-10 mx-auto text-muted-foreground/50" />
              <div className="mt-2">
                {user.role === "buyer" ? "No sample orders yet. Look for the 'Order sample' box on any approved product."
                  : user.role === "supplier" ? "No sample orders yet. Enable samples on your products to let buyers try before committing."
                  : "No sample orders yet."}
              </div>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {items.map((o) => (
                <div key={o.id} className="py-4 flex items-center justify-between gap-4 flex-wrap" data-testid={`sample-${o.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-secondary">{o.product_name}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      Qty {o.quantity} · ${o.amount?.toFixed(2)} {o.currency?.toUpperCase()} · Ship to {o.shipping_city}, {o.shipping_country}
                      {o.tracking && <> · Tracking <code className="font-mono text-secondary">{o.tracking}</code></>}
                    </div>
                    {o.notes && <div className="text-xs text-muted-foreground mt-1 italic">Note: {o.notes}</div>}
                  </div>
                  <div className="flex items-center gap-3">
                    <StatusPill status={o.status} />
                    {user.role === "supplier" && o.status === "paid" && <ShipDialog order={o} onSaved={load} />}
                    {user.role === "supplier" && o.status === "shipped" && (
                      <Button size="sm" variant="outline" onClick={async () => {
                        try { await api.patch(`/supplier/sample-orders/${o.id}`, { status: "delivered" }); toast.success("Marked delivered"); load(); }
                        catch { toast.error("Failed"); }
                      }} data-testid={`deliver-${o.id}`}>Mark delivered</Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </DashboardLayout>
  );
}
